/**
 * InviteUserModal Component
 * Modal for inviting new users to the organization
 */

import { useState } from 'react'
import { X, Mail, User, Shield, Send, AlertCircle, CheckCircle } from 'lucide-react'
import type { UserRole } from '@/types/user.types'
import { getRoleDisplayName } from '@/types/auth.types'

// ============================================================================
// TYPES
// ============================================================================

interface InviteUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: InviteFormData) => Promise<void>
  availableRoles?: UserRole[]
  tenantName?: string
}

export interface InviteFormData {
  email: string
  fullName?: string
  role: UserRole
  message?: string
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_ROLES: UserRole[] = [
  'firm_admin',
  'ca_partner',
  'technical_assistant',
  'research_assistant',
  'individual_client',
  'business_owner_client',
  'authorized_client',
]

const ROLE_DESCRIPTIONS: Partial<Record<UserRole, string>> = {
  firm_admin: 'Manage users and settings without billing ownership',
  ca_partner: 'Manage portfolios, filings, reviews, and approvals',
  technical_assistant: 'Work on assigned tax preparation and documents',
  research_assistant: 'Prepare sourced research and advisory drafts',
  individual_client: 'Access one personal taxpayer relationship',
  business_owner_client: 'Access explicitly linked business entities',
  authorized_client: 'Use time-bound, action-scoped representative access',
}

// ============================================================================
// COMPONENT
// ============================================================================

export function InviteUserModal({
  isOpen,
  onClose,
  onSubmit,
  availableRoles = DEFAULT_ROLES,
  tenantName = 'the organization',
}: InviteUserModalProps) {
  const [formData, setFormData] = useState<InviteFormData>({
    email: '',
    fullName: '',
    role: 'technical_assistant',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)

  const resetForm = () => {
    setFormData({
      email: '',
      fullName: '',
      role: 'assistant',
      message: '',
    })
    setError('')
    setIsSuccess(false)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation
    if (!formData.email.trim()) {
      setError('Email is required')
      return
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)

    try {
      await onSubmit(formData)
      setIsSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field: keyof InviteFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleInviteAnother = () => {
    resetForm()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-dark rounded-xl shadow-2xl border border-surface-overlay w-full max-w-md mx-4 max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-overlay">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Send className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Invite User</h2>
              <p className="text-sm text-text-tertiary">Send an invitation to join {tenantName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-ghost btn-sm btn-square"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isSuccess ? (
          /* Success State */
          <div className="p-6 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">Invitation Sent!</h3>
              <p className="text-sm text-text-secondary">
                An invitation email has been sent to
                <br />
                <span className="font-medium text-text-primary">{formData.email}</span>
              </p>
            </div>
            <p className="text-xs text-text-tertiary">
              The invitation will expire in 7 days
            </p>

            {/* Demo Notice */}
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                <strong>Demo Mode:</strong> No actual email is sent. The invitation is mocked.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={handleInviteAnother} className="btn btn-outline flex-1">
                Invite Another
              </button>
              <button type="button" onClick={handleClose} className="btn btn-primary flex-1">
                Done
              </button>
            </div>
          </div>
        ) : (
          /* Form State */
          <>
            <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Error Alert */}
              {error && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="user@example.com"
                    className="input input-bordered w-full pl-10"
                    disabled={isSubmitting}
                    required
                    autoFocus
                  />
                </div>
              </div>

              {/* Full Name (optional) */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Full Name <span className="text-text-tertiary">(optional)</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    placeholder="Enter their name"
                    className="input input-bordered w-full pl-10"
                    disabled={isSubmitting}
                  />
                </div>
                <p className="text-xs text-text-tertiary mt-1">
                  They can update their name when they accept the invitation
                </p>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Role <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                  <select
                    value={formData.role}
                    onChange={(e) => handleChange('role', e.target.value as UserRole)}
                    className="select select-bordered w-full pl-10"
                    disabled={isSubmitting}
                    required
                  >
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>
                        {getRoleDisplayName(role)}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-text-tertiary mt-1">
                  {ROLE_DESCRIPTIONS[formData.role]}
                </p>
              </div>

              {/* Personal Message (optional) */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Personal Message <span className="text-text-tertiary">(optional)</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Add a personal note to the invitation..."
                  rows={3}
                  className="textarea textarea-bordered w-full resize-none"
                  disabled={isSubmitting}
                />
              </div>

              {/* Info Box */}
              <div className="p-3 rounded-lg bg-surface-overlay/50 text-xs text-text-secondary">
                <p>
                  The invited user will receive an email with a link to create their account. The
                  invitation will expire in 7 days.
                </p>
              </div>
            </form>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-surface-overlay bg-surface-overlay/30">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-ghost"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="btn btn-primary gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default InviteUserModal
